/**
 * IntegratrManager - External API integration and webhook management
 * 
 * CHANGE HISTORY:
 * - 251102_1642 danielbeer: Integrate Profylr for external API context
 * - 251023_2039 [pending] danielbeer: Initial creation
 * 
 * Handles outbound API calls, inbound webhooks, and outbound webhooks
 */

import type { ProfileManager, ResolvedProfile } from '@opcentral/profylr';
import type {
  APICallConfig,
  APIResponse,
  WebhookConfig,
  WebhookPayload,
  WebhookDelivery,
  InboundWebhookConfig,
  WebhookSubscription,
  IntegratrConfig,
  IntegratrEvents,
  RateLimitState
} from '../types';

export class IntegratrManager {
  private config: IntegratrConfig;
  private events: IntegratrEvents;
  
  // Webhooks
  private webhooks: Map<string, WebhookConfig> = new Map();
  private inboundWebhooks: Map<string, InboundWebhookConfig> = new Map();
  private subscriptions: Map<string, WebhookSubscription[]> = new Map();
  private deliveries: Map<string, WebhookDelivery> = new Map();
  
  // Rate limiting
  private rateLimitState: RateLimitState = {
    requests: [],
    blocked: false,
    nextReset: 0
  };
  
  // Integration dependencies
  private actr: any;
  private loggr: any;
  private profylr: ProfileManager | null;
  
  constructor(config: IntegratrConfig = {}, events: IntegratrEvents = {}) {
    this.config = {
      timeout: 30000,
      retries: 3,
      retryDelay: 1000,
      ...config
    };
    this.events = events;
    
    this.actr = config.actr;
    this.loggr = config.loggr;
    this.profylr = config.profylr || null;
    
    // Register Actr actions if available
    if (this.actr) {
      this.registerActions();
    }
    
    this.log('info', 'IntegratrManager initialized');
  }
  
  /**
   * Get active profile from Profylr
   */
  async getActiveProfile(): Promise<ResolvedProfile | null> {
    if (!this.profylr) {
      return null;
    }
    
    try {
      return await this.profylr.getActiveProfile();
    } catch (error) {
      this.log('warn', 'Failed to get active profile', error);
      return null;
    }
  }

  /**
   * Apply profile context to API call config
   * Enhances headers and params with profile context (compliance, brand, etc.)
   */
  private async applyProfileContext(config: APICallConfig): Promise<APICallConfig> {
    const profile = await this.getActiveProfile();
    
    if (!profile?.context) {
      return config; // No profile or no context
    }
    
    const enhancedConfig = { ...config };
    const context = profile.context;
    
    // Add context to headers
    const contextHeaders: Record<string, string> = {};
    
    if (context.complianceLevel) {
      contextHeaders['X-Compliance-Level'] = context.complianceLevel;
    }
    if (context.industry) {
      contextHeaders['X-Industry'] = context.industry;
    }
    if (context.brandVoice) {
      contextHeaders['X-Brand-Voice'] = context.brandVoice;
    }
    
    // Merge with existing headers
    enhancedConfig.headers = {
      ...config.headers,
      ...contextHeaders,
    };
    
    // Add context to params if not already present
    if (config.params) {
      enhancedConfig.params = {
        ...config.params,
        _profileContext: context,
      };
    }
    
    return enhancedConfig;
  }

  /**
   * Make an API call with profile context
   * 
   * PROFYLR INTEGRATION:
   * 1. Get active profile from Profylr
   * 2. Apply profile context to API call (headers, params)
   * 3. Execute API call with enhanced context
   */
  async call<T = any>(config: APICallConfig): Promise<APIResponse<T>> {
    const startTime = Date.now();
    
    // Check rate limit
    if (this.config.rateLimit && !this.checkRateLimit()) {
      return {
        success: false,
        data: null as any,
        status: 429,
        statusText: 'Too Many Requests',
        headers: {},
        responseTime: 0,
        error: 'Rate limit exceeded'
      };
    }
    
    // Apply profile context to config
    const enhancedConfig = await this.applyProfileContext(config);
    
    this.events.onAPICall?.(enhancedConfig);
    this.log('info', `API call: ${enhancedConfig.method || 'GET'} ${enhancedConfig.endpoint}`);
    
    const fullConfig = {
      timeout: this.config.timeout,
      retries: this.config.retries,
      retryDelay: this.config.retryDelay,
      ...enhancedConfig
    };
    
    let lastError: Error | null = null;
    
    for (let attempt = 0; attempt <= (fullConfig.retries || 0); attempt++) {
      try {
        const response = await this.makeRequest<T>(fullConfig);
        
        // Track rate limit
        if (this.config.rateLimit) {
          this.trackRequest();
        }
        
        this.events.onAPIResponse?.(response);
        this.log('info', `API response: ${response.status} ${response.statusText}`);
        
        return response;
        
      } catch (error) {
        lastError = error;
        this.log('error', `API call failed (attempt ${attempt + 1})`, error);
        
        if (attempt < (fullConfig.retries || 0)) {
          await this.delay(fullConfig.retryDelay || 1000);
        }
      }
    }
    
    this.events.onAPIError?.(lastError!);
    
    return {
      success: false,
      data: null as any,
      status: 0,
      statusText: 'Request Failed',
      headers: {},
      responseTime: Date.now() - startTime,
      error: lastError?.message || 'Unknown error'
    };
  }
  
  /**
   * Make the actual HTTP request
   */
  private async makeRequest<T>(config: APICallConfig): Promise<APIResponse<T>> {
    const startTime = Date.now();
    
    // Build URL
    let url = config.endpoint;
    if (this.config.baseURL && !url.startsWith('http')) {
      url = `${this.config.baseURL}${url}`;
    }
    
    // Add query params
    if (config.params) {
      const params = new URLSearchParams(config.params);
      url += `?${params.toString()}`;
    }
    
    // Build headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...this.config.defaultHeaders,
      ...config.headers
    };
    
    // Build fetch options
    const options: RequestInit = {
      method: config.method || 'GET',
      headers,
      signal: AbortSignal.timeout(config.timeout || this.config.timeout || 30000)
    };
    
    if (config.data && ['POST', 'PUT', 'PATCH'].includes(options.method!)) {
      options.body = JSON.stringify(config.data);
    }
    
    // Make request
    const response = await fetch(url, options);
    const responseTime = Date.now() - startTime;
    
    // Parse response
    let data: T;
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text() as any;
    }
    
    // Build response headers
    const responseHeaders: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      responseHeaders[key] = value;
    });
    
    return {
      success: response.ok,
      data,
      status: response.status,
      statusText: response.statusText,
      headers: responseHeaders,
      responseTime
    };
  }
  
  /**
   * Register an outbound webhook
   */
  registerWebhook(config: WebhookConfig): void {
    this.webhooks.set(config.id, {
      active: true,
      retries: 3,
      retryDelay: 1000,
      ...config
    });
    
    this.log('info', `Webhook registered: ${config.id}`);
  }
  
  /**
   * Unregister a webhook
   */
  unregisterWebhook(id: string): boolean {
    const deleted = this.webhooks.delete(id);
    if (deleted) {
      this.log('info', `Webhook unregistered: ${id}`);
    }
    return deleted;
  }
  
  /**
   * Send webhook to registered endpoints
   */
  async sendWebhook(event: string, data): Promise<WebhookDelivery[]> {
    const deliveries: WebhookDelivery[] = [];
    
    // Find webhooks listening for this event
    for (const [id, webhook] of this.webhooks) {
      if (!webhook.active || !webhook.events.includes(event)) {
        continue;
      }
      
      const delivery = await this.deliverWebhook(webhook, event, data);
      deliveries.push(delivery);
    }
    
    return deliveries;
  }
  
  /**
   * Deliver webhook to a specific endpoint
   */
  private async deliverWebhook(
    webhook: WebhookConfig,
    event: string,
    data: any
  ): Promise<WebhookDelivery> {
    const deliveryId = `delivery_${Date.now()}_${Math.random()}`;
    
    const payload: WebhookPayload = {
      event,
      timestamp: Date.now(),
      data
    };
    
    // Sign payload if secret provided
    if (webhook.secret) {
      payload.signature = await this.signPayload(payload, webhook.secret);
    }
    
    const delivery: WebhookDelivery = {
      id: deliveryId,
      webhookId: webhook.id,
      event,
      payload,
      url: webhook.url,
      status: 'pending',
      attempts: 0
    };
    
    this.deliveries.set(deliveryId, delivery);
    
    // Attempt delivery with retries
    for (let attempt = 0; attempt <= (webhook.retries || 0); attempt++) {
      delivery.attempts = attempt + 1;
      delivery.lastAttempt = Date.now();
      
      try {
        const response = await this.call({
          endpoint: webhook.url,
          method: 'POST',
          headers: webhook.headers,
          data: payload,
          timeout: 10000,
          retries: 0
        });
        
        delivery.response = {
          status: response.status,
          statusText: response.statusText,
          body: response.data
        };
        
        if (response.success) {
          delivery.status = 'success';
          this.events.onWebhookSent?.(delivery);
          this.log('info', `Webhook delivered: ${webhook.id} -> ${event}`);
          return delivery;
        } else {
          delivery.error = `HTTP ${response.status}: ${response.statusText}`;
        }
        
      } catch (error) {
        delivery.error = error.message;
      }
      
      // Retry if not last attempt
      if (attempt < (webhook.retries || 0)) {
        delivery.status = 'retrying';
        delivery.nextRetry = Date.now() + (webhook.retryDelay || 1000);
        await this.delay(webhook.retryDelay || 1000);
      }
    }
    
    delivery.status = 'failed';
    this.events.onWebhookFailed?.(delivery);
    this.log('error', `Webhook delivery failed: ${webhook.id} -> ${event}`, delivery.error);
    
    return delivery;
  }
  
  /**
   * Register an inbound webhook handler
   */
  registerInboundWebhook(config: InboundWebhookConfig): void {
    this.inboundWebhooks.set(config.id, {
      validateSignature: true,
      signatureHeader: 'x-webhook-signature',
      signatureAlgorithm: 'sha256',
      ...config
    });
    
    this.log('info', `Inbound webhook registered: ${config.id} at ${config.path}`);
  }
  
  /**
   * Handle inbound webhook
   */
  async handleInboundWebhook(
    webhookId: string,
    payload,
    headers: Record<string, string>
  ): Promise<void> {
    const webhook = this.inboundWebhooks.get(webhookId);
    if (!webhook) {
      throw new Error(`Inbound webhook not found: ${webhookId}`);
    }
    
    // Validate signature if required
    if (webhook.validateSignature && webhook.secret) {
      const signature = headers[webhook.signatureHeader || 'x-webhook-signature'];
      if (!signature) {
        throw new Error('Missing webhook signature');
      }
      
      const expectedSignature = await this.signPayload(payload, webhook.secret);
      if (signature !== expectedSignature) {
        throw new Error('Invalid webhook signature');
      }
    }
    
    this.events.onWebhookReceived?.(payload);
    this.log('info', `Inbound webhook received: ${webhookId}`);
    
    // Call handler
    await webhook.handler(payload);
  }
  
  /**
   * Subscribe to webhook events
   */
  subscribe(event: string, handler: (data) => Promise<void> | void, filter?: (data) => boolean): string {
    const subscriptionId = `sub_${Date.now()}_${Math.random()}`;
    
    const subscription: WebhookSubscription = {
      id: subscriptionId,
      event,
      handler,
      filter
    };
    
    if (!this.subscriptions.has(event)) {
      this.subscriptions.set(event, []);
    }
    
    this.subscriptions.get(event)!.push(subscription);
    this.log('info', `Subscribed to event: ${event}`);
    
    return subscriptionId;
  }
  
  /**
   * Unsubscribe from webhook events
   */
  unsubscribe(subscriptionId: string): boolean {
    for (const [event, subs] of this.subscriptions) {
      const index = subs.findIndex(s => s.id === subscriptionId);
      if (index !== -1) {
        subs.splice(index, 1);
        this.log('info', `Unsubscribed: ${subscriptionId}`);
        return true;
      }
    }
    return false;
  }
  
  /**
   * Emit event to subscribers
   */
  async emit(event: string, data): Promise<void> {
    const subs = this.subscriptions.get(event) || [];
    
    for (const sub of subs) {
      // Apply filter if provided
      if (sub.filter && !sub.filter(data)) {
        continue;
      }
      
      try {
        await sub.handler(data);
      } catch (error) {
        this.log('error', `Subscription handler error: ${sub.id}`, error);
      }
    }
    
    // Also send to registered webhooks
    await this.sendWebhook(event, data);
  }
  
  /**
   * Sign webhook payload
   */
  private async signPayload(payload, secret: string): Promise<string> {
    const data = JSON.stringify(payload);
    const encoder = new TextEncoder();
    const keyData = encoder.encode(secret);
    const messageData = encoder.encode(data);
    
    const key = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    
    const signature = await crypto.subtle.sign('HMAC', key, messageData);
    const hashArray = Array.from(new Uint8Array(signature));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    return hashHex;
  }
  
  /**
   * Check rate limit
   */
  private checkRateLimit(): boolean {
    if (!this.config.rateLimit) {
      return true;
    }
    
    const now = Date.now();
    const { maxRequests, perMilliseconds } = this.config.rateLimit;
    
    // Clean old requests
    this.rateLimitState.requests = this.rateLimitState.requests.filter(
      time => now - time < perMilliseconds
    );
    
    // Check if blocked
    if (this.rateLimitState.requests.length >= maxRequests) {
      this.rateLimitState.blocked = true;
      this.rateLimitState.nextReset = now + perMilliseconds;
      return false;
    }
    
    this.rateLimitState.blocked = false;
    return true;
  }
  
  /**
   * Track request for rate limiting
   */
  private trackRequest(): void {
    if (this.config.rateLimit) {
      this.rateLimitState.requests.push(Date.now());
    }
  }
  
  /**
   * Delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  
  /**
   * Register Actr actions
   */
  private registerActions(): void {
    if (!this.actr) return;
    
    // API actions
    this.actr.registerAction('integratr:call', async (params) => {
      return this.call(params);
    });
    
    // Webhook actions
    this.actr.registerAction('integratr:send-webhook', async (params) => {
      return this.sendWebhook(params.event, params.data);
    });
    
    this.actr.registerAction('integratr:emit', async (params) => {
      return this.emit(params.event, params.data);
    });
    
    this.log('info', 'Actr actions registered');
  }
  
  /**
   * Logging helper
   */
  private log(level: string, message: string, data?: any): void {
    if (this.loggr) {
      this.loggr[level](`[Integratr] ${message}`, data);
    } else if (level === 'error') {
      console.error(`[Integratr] ${message}`, data);
    }
  }
  
  /**
   * Get webhook delivery status
   */
  getDelivery(deliveryId: string): WebhookDelivery | undefined {
    return this.deliveries.get(deliveryId);
  }
  
  /**
   * List all webhooks
   */
  listWebhooks(): WebhookConfig[] {
    return Array.from(this.webhooks.values());
  }
  
  /**
   * List all inbound webhooks
   */
  listInboundWebhooks(): InboundWebhookConfig[] {
    return Array.from(this.inboundWebhooks.values());
  }
  
  /**
   * Get rate limit status
   */
  getRateLimitStatus(): RateLimitState {
    return { ...this.rateLimitState };
  }
}

// Singleton instance
let integratrInstance: IntegratrManager | null = null;

export function getIntegratrInstance(config?: IntegratrConfig, events?: IntegratrEvents): IntegratrManager {
  if (!integratrInstance) {
    integratrInstance = new IntegratrManager(config, events);
  }
  return integratrInstance;
}

export function resetIntegratrInstance(): void {
  integratrInstance = null;
}
